/**
 * A no-operation synchronous function. Does nothing and returns undefined.
 */
export const noopSync = () => { };
/**
 * A no-operation asynchronous function. Returns a resolved Promise.
 * @returns {Promise<void>} A resolved promise.
 */
export const noop = () => Promise.resolve();
/**
 * Identity function that returns the value passed to it.
 * @template T
 * @param {T} val - The value to return.
 * @returns {T} The same value.
 */
export const pass = (val) => val;
/**
 * Returns a function that always resolves with the given value.
 * @template T
 * @param {T} val - The value to always return.
 * @returns {() => Promise<T>} A function returning a resolved promise with the value.
 */
export const always = (val) => () => Promise.resolve(val);
/**
 * Returns a function that always returns the given value synchronously.
 * @template T
 * @param {T} val - The value to always return.
 * @returns {() => T} A function returning the value.
 */
export const alwaysSync = (val) => () => val;
/**
 * Trims whitespace from both ends of a string.
 * @param {string} str - The string to trim.
 * @returns {string} The trimmed string.
 */
export const trim = (str) => str.trim();
/**
 * Checks if a value is considered empty.
 * - Empty array
 * - Empty object
 * - Falsy value (null, undefined, '', 0, false)
 * @param {unknown} val - The value to check.
 * @returns {boolean} True if the value is empty, false otherwise.
 */
export const empty = (val) => (Array.isArray(val) && !val.length)
    || (typeof val === 'object' && val !== null && !Object.keys(val).length)
    || !val;
/**
 * Checks if a value is not empty.
 * @param {unknown} val - The value to check.
 * @returns {boolean} True if the value is not empty, false otherwise.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const notEmpty = (val) => !empty(val);
/**
 * Executes one of two functions based on a boolean condition.
 * @template T
 * @param {boolean} value - The condition to evaluate.
 * @param {() => T} fnIf - Function to execute if condition is true.
 * @param {() => T} [fnElse] - Function to execute if condition is false.
 * @returns {T} The result of the executed function.
 */
export const cond = (value, fnIf, fnElse) => (value ? fnIf : fnElse || noopSync)();
/**
 * Executes one of two functions based on the result of a condition function.
 * @template T
 * @param {() => boolean} valueFn - Function that returns a boolean.
 * @param {() => T} fnIf - Function to execute if condition is true.
 * @param {() => T} [fnElse] - Function to execute if condition is false.
 * @returns {() => T} A function that returns the result of the executed function.
 */
export const condFn = (valueFn, fnIf, fnElse) => () => (valueFn() ? fnIf : fnElse || noopSync)();
/**
 * Returns a function that throws the given error when called.
 * @param {Error} err - The error to throw.
 * @returns {() => never} A function that throws the error.
 */
export const throwFn = (err) => () => {
    throw err;
};
/**
 * Asynchronously waits for a specified number of milliseconds.
 *
 * @param {number} [ms] - The number of milliseconds to wait. If omitted, the delay is effectively 0.
 * @returns {Promise<void>} A promise that resolves after the specified delay.
 */
export const wait = (ms) => new Promise((resolve) => {
    setTimeout(() => resolve(), ms);
});
/**
 * Waits until a condition is met.
 * @param {Function} valueFn - The function that returns the value to check.
 * @param {number} interval - The interval in milliseconds to wait between checks.
 * @returns {Promise} A promise that resolves when the condition is met.
 */
export const waitUntil = (valueFn, interval = 10) => cond(valueFn(), always(true), async () => {
    await wait(interval);
    return waitUntil(valueFn, interval);
});
export class AlixError extends Error {
}
/**
 * Checks if an error is an internal error.
 * @param {Error | AlixError} err - The error to check.
 * @returns {boolean} True if the error is an internal error, false otherwise.
 */
export const isInternalError = (err) => err instanceof AlixError;
export class AlixStopError extends Error {
}
/**
 * Checks if an error is an internal stop error.
 * @param {Error | AlixStopError} err - The error to check.
 * @returns {boolean} True if the error is an internal stop error, false otherwise.
 */
export const isInternalStop = (err) => err instanceof AlixStopError;
/**
 * Throws an error when a condition is met.
 * @param {boolean} condValue - The condition value.
 * @param {string | Error | Function} message - The error message or error object or a function that returns the message or error.
 * @param {boolean} [stopEverything=true] - Whether to stop everything.
 * @param {object} [errorData] - Additional error data.
 * @throws {AlixError} The error object.
 */
export const errorWhen = (condValue, message, stopEverything = true, errorData) => cond(condValue, () => {
    const msg = typeof message === 'function' ? message() : message;
    const error = typeof msg === 'object' ? msg : new AlixError(msg);
    error.stopEverything = stopEverything;
    cond(!!errorData, () => Object.assign(error, errorData));
    throw error;
});
/**
 * Throws an AlixStopError when a condition is met.
 * @param {boolean} condValue - The condition value.
 * @throws {AlixStopError} The stop error object.
 */
export const stopWhen = (condValue) => cond(condValue, () => {
    throw new AlixStopError();
});
/**
 * Handles stop errors in a function.
 * @param {Function} fn - The function to wrap.
 * @returns {Function} The wrapped function.
 */
// eslint-disable-next-line consistent-return, @typescript-eslint/no-explicit-any
export const handleStop = (fn) => (...args) => {
    try {
        return fn(...args);
    }
    catch (err) {
        errorWhen(!isInternalStop(err), err);
    }
};
/**
 * Checks if an error should stop everything.
 * @param {Error | AlixError} err - The error to check.
 * @returns {boolean} True if the error should stop everything, false otherwise.
 */
export const hasToStopEverything = (err) => typeof err.stopEverything === 'undefined'
    || err.stopEverything;
/**
 * Curries a function with the provided arguments.
 * @param {Function} fn - The function to curry.
 * @param {...unknown[]} argsToCurry - The arguments to curry.
 * @returns {Function} The curried function.
 */
export const curry = (...argsToCurry) => {
    const fn = argsToCurry.pop();
    return (...args) => fn(...argsToCurry, ...args);
};
/**
 * Curries a function with the provided arguments.
 * @param {Function} fn - The function to curry.
 * @param {...unknown[]} argsToCurry - The arguments to curry.
 * @returns {Function} The curried function.
 */
export const curry2 = (fn, ...argsToCurry) => (...args) => fn(...argsToCurry, ...args);
/**
 * Creates a function that calls another function with the provided arguments.
 * @param {...unknown[]} beforeArgs - The arguments to pass before the function arguments.
 * @returns {Function} The new function.
 */
export const withFn = (...beforeArgs) => (fn) => (...args) => fn(...beforeArgs, ...args);
/**
 * Capitalizes the first letter of a string.
 * @param {string} [str=''] - The string to capitalize.
 * @returns {string} The capitalized string.
 */
export const upperFirstLetter = (str = '') => str.charAt(0).toUpperCase() + str.slice(1);
/**
 * Generates a random number between min and max.
 * @param {number} min - The minimum value.
 * @param {number} max - The maximum value.
 * @returns {number} The random number.
 */
export const randomBetween = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
/**
 * Selects a random item from an array.
 * @param {Array} array - The array to select from.
 * @returns {*} The random item.
 */
export const randomItem = (array) => array[Math.floor(Math.random() * array.length)];
/**
 * Generates a random string from the given characters.
 * @param {string} characters - The characters to use.
 * @param {number} length - The length of the string.
 * @returns {string} The random string.
 */
export const randomFromString = (characters, length) => {
    let result = '';
    while (result.length < length) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
};
/**
 * Generates a random string of uppercase letters.
 * @param {number} length - The length of the string.
 * @returns {string} The random string.
 */
export const randomUpperAlphaLetters = (length) => randomFromString('ABCDEFGHIJKLMNOPQRSTUVWXYZ', length);
/**
 * Generates a random numeric string.
 * @param {number} length - The length of the string.
 * @param {boolean} [beginWithZero=true] - Whether the string can begin with zero.
 * @returns {string} The random numeric string.
 */
export const randomNumeric = (length, beginWithZero = true) => [
    !beginWithZero && length > 0 ? randomFromString('123456789', 1) : '',
    randomFromString('0123456789', length - (!beginWithZero ? 1 : 0)),
].join('');
/**
 * Retries a function a specified number of times.
 * @param {Object} options - The options for the retry.
 * @param {number} options.times - The number of times to retry.
 * @param {Function} options.fn - The function to retry.
 * @param {number} [options.waitMs=0] - The wait time between retries.
 * @param {Function} [options.onError] - The function to call on error.
 * @param {Function} [options.onCheckErrorForRetry] - The function to check if an error should be retried.
 * @param {number} [options.tries=0] - The current number of tries.
 * @returns {Promise} A promise that resolves with the result of the function.
 */
export const retry = async ({ times, fn, waitMs = 0, onError, onCheckErrorForRetry = () => true, tries = 0 }) => {
    try {
        return await fn(tries);
    }
    catch (err) {
        return cond(onCheckErrorForRetry(err, tries) && tries <= times, async () => {
            await wait(waitMs);
            return retry({ times, fn, waitMs, onError, onCheckErrorForRetry, tries: tries + 1 });
        }, () => (onError || ((e) => throwFn(e)()))(err));
    }
};
/**
 * Nests an object within another object.
 * @param {Object} obj - The object to nest.
 * @param {...string} args - The keys to nest the object under.
 * @returns {*} The nested object.
 */
export const nest = (obj, ...args) => args
    .reduce((acc, level) => acc && acc[level], obj);
/**
 * Creates a URLSearchParams object from a body object.
 * @param {Object} body - The body object.
 * @returns {URLSearchParams} The URLSearchParams object.
 */
export const urlSearchParams = (body) => new URLSearchParams(body);
/**
 * Executes a function for each element in an array.
 * @param {Array} arr - The array to iterate over.
 * @param {Function} fn - The function to execute for each element.
 * @param {boolean} [continueOnError=false] - Whether to continue on error.
 * @returns {Promise} A promise that resolves when all functions have been executed.
 */
export const each = (arr, fn, continueOnError = false) => {
    const eachResolve = (i = 0) => cond(i < arr.length, async () => {
        try {
            await fn(arr[i], i);
        }
        catch (err) {
            errorWhen(!continueOnError, err);
        }
        return eachResolve(i + 1);
    });
    return eachResolve();
};
/**
 * Maps an array to a new array using a function.
 * @param {Array} arr - The array to map.
 * @param {Function} fn - The function to execute for each element.
 * @returns {Promise} A promise that resolves with the new array.
 */
export const map = (arr, fn) => {
    const mapResolve = (i = 0, newArr = []) => cond(i < arr.length, async () => mapResolve(i + 1, newArr.concat([await fn(arr[i], i)])), always(newArr));
    return mapResolve();
};
/**
 * Reduces an array to a single value using a function.
 * @param {Array} arr - The array to reduce.
 * @param {Function} fn - The function to execute for each element.
 * @param {*} initialValue - The initial value.
 * @returns {Promise} A promise that resolves with the reduced value.
 */
export const reduce = (arr, fn, initialValue) => {
    const reduceResolve = (i, acc) => cond(i < arr.length, async () => reduceResolve(i + 1, await fn(acc, arr[i], i)), always(acc));
    return reduceResolve(0, initialValue);
};
/**
 * Executes a function for each chunk of an array.
 * @param {number} chunkSize - The size of each chunk.
 * @param {Array} arr - The array to iterate over.
 * @param {Function} fn - The function to execute for each element.
 * @returns {Promise} A promise that resolves when all functions have been executed.
 */
export const eachChunk = (chunkSize, arr, fn) => {
    const eachChunkResolve = (i = 0) => cond(i < arr.length, async () => {
        await Promise.all(arr
            .slice(i, i + chunkSize)
            .map((value, j) => fn(value, i + j)));
        return eachChunkResolve(i + chunkSize);
    });
    return eachChunkResolve();
};
/**
 * Filters out properties with undefined values from an object.
 * @template T
 * @param {T} obj - The object to filter.
 * @returns {T} - The filtered object.
 */
export const filterUndefined = (obj) => Object.keys(obj)
    .reduce((acc, key) => (obj[key] === undefined ? acc : { ...acc, [key]: obj[key] }), {});
/**
 * Creates a clone of an object with only the specified attributes.
 * Optionally removes properties with undefined values.
 * @template T, TT
 * @param {string[]} attributesNames - The names of the attributes to include in the clone.
 * @param {boolean} [removeUndefined=true] - Whether to remove properties with undefined values.
 * @returns {(obj: T) => TT} - The function that creates the clone.
 */
export const cloneLimited = (attributesNames, removeUndefined = true) => (obj) => {
    const clone = attributesNames.reduce((acc, key) => ({ ...acc, [key]: obj[key] }), {});
    return (removeUndefined ? filterUndefined(clone) : clone);
};
/**
 * Splits an array into chunks of the specified size.
 * @template T
 * @param {number} chunkSize - The size of each chunk.
 * @param {T[]} array - The array to split.
 * @returns {T[][]} - The array of chunks.
 */
export const chunksFromArray = (chunkSize, array) => cond(chunkSize > 0, () => {
    const splittedArray = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        splittedArray.push(array.slice(i, i + chunkSize));
    }
    return splittedArray;
}, alwaysSync([]));
/**
 * Checks if a promise is fulfilled.
 * @template T
 * @param {PromiseSettledResult<T>} p - The promise to check.
 * @returns {p is PromiseFulfilledResult<T>} - True if the promise is fulfilled, false otherwise.
 */
export const isFulfilled = (p) => p.status === 'fulfilled';
/**
 * Checks if a promise is rejected.
 * @template T
 * @param {PromiseSettledResult<T>} p - The promise to check.
 * @returns {p is PromiseRejectedResult} - True if the promise is rejected, false otherwise.
 */
export const isRejected = (p) => p.status === 'rejected';
/**
 * Resolves an array of promises and returns the fulfilled results.
 * Throws an error if any promise is rejected.
 * @template T
 * @param {Promise<T>[]} asynFns - The array of promises to resolve.
 * @returns {Promise<T[]>} - The array of fulfilled results.
 */
export const resolveAll = async (asynFns) => {
    const results = await Promise.allSettled(asynFns);
    const errors = results.filter(isRejected).map((result) => result.reason);
    errorWhen(!!errors.length, `resolveAll: ${errors}`);
    return results.filter(isFulfilled).map((result) => result.value);
};
/**
 * Resolves an array of promises and returns the fulfilled results and errors.
 * @template T
 * @param {Promise<T>[]} asynFns - The array of promises to resolve.
 * @returns {Promise<{ success: T[], errors: Error[] }>} - The object containing the fulfilled results and errors.
 */
export const resolveWithErrors = async (asynFns) => {
    const results = await Promise.allSettled(asynFns);
    return {
        success: results.filter(isFulfilled).map((result) => result.value),
        errors: results.filter(isRejected).map((result) => result.reason),
    };
};
/**
 * Executes a function that can't throw an error. Instead, this function returns the result or the error stoped in an object.
 * @template T
 * @param {() => T | void} fn - The function to execute.
 * @returns {{ success: boolean, result?: T, error?: unknown }} - The result or error.
 */
export const safe = (fn) => {
    try {
        return { success: true, result: fn() };
    }
    catch (err) {
        return { success: false, error: err };
    }
};
/**
 * Executes an asynchronous function that can't throw an error. Instead, this function returns the result or the error stoped in an object.
 * @template T
 * @param {() => Promise<T | void>} fn - The asynchronous function to execute.
 * @param {(err: unknown) => void} [onError] - The function to call on error.
 * @returns {Promise<{ success: boolean, result?: T, error?: unknown }>} - The result or error.
 */
export const safeAsync = async (fn, onError) => {
    try {
        return { success: true, result: await fn() };
    }
    catch (err) {
        (onError || noop)(err);
        return { success: false, error: err };
    }
};
/**
 * Creates a clone of an object without the specified keys.
 * @template T, K
 * @param {T} obj - The object to clone.
 * @param {K[]} keys - The keys to omit.
 * @returns {Omit<T, K>} - The cloned object without the specified keys.
 */
export const omit = (obj, keys) => {
    const clone = { ...obj };
    keys.forEach((key) => delete clone[key]);
    return clone;
};
/**
 * Finds the minimum value in an array based on a function.
 * @template T
 * @param {T[]} arr - The array to search.
 * @param {(item: T) => number} fn - The function to apply to each item.
 * @returns {number} - The minimum value.
 */
export const minIn = (arr, fn) => arr.reduce((acc, item, i) => {
    const value = fn(item);
    return Math.min(i === 0 ? value + 1 : acc, value);
}, 0);
/**
 * Finds the maximum value in an array based on a function.
 * @template T
 * @param {T[]} arr - The array to search.
 * @param {(item: T) => number} [fn] - The function to apply to each item.
 * @returns {number} - The maximum value.
 */
export const maxIn = (arr, fn) => arr.reduce((acc, item, i) => {
    const value = fn ? fn(item) : item.length;
    return Math.max(i === 0 ? value - 1 : acc, value);
}, 0);
/**
 * Creates an array of unique objects based on a property.
 * @template T
 * @param {T[]} arr - The array to filter.
 * @param {string} prop - The property to use for uniqueness.
 * @returns {T[]} - The array of unique objects.
 */
export const uniqueArrayByProperty = (arr, prop) => [
    ...new Map(arr.map((item) => [item[prop], item])).values(),
];
/**
 * Creates a comparison function for sorting objects by an attribute.
 * @param {string} attr - The attribute to sort by.
 * @param {'ASC' | 'DESC'} [order='ASC'] - The sort order.
 * @returns {(a: T, b: T) => number} - The comparison function.
 */
export const sortByFn = (attr, order = 'ASC') => (a, b) => {
    const orderValues = [{ ASC: 1, DESC: -1 }, { ASC: -1, DESC: 1 }];
    let result = 0;
    result = a[attr] > b[attr] ? orderValues[0][order] : result;
    result = a[attr] < b[attr] ? orderValues[1][order] : result;
    return result;
};
/**
 * Sorts an array of objects by an attribute.
 * @template T
 * @param {T[]} arr - The array to sort.
 * @param {string} attr - The attribute to sort by.
 * @param {'ASC' | 'DESC'} [order='ASC'] - The sort order.
 * @returns {T[]} - The sorted array.
 */
export const sortBy = (arr, attr, order = 'ASC') => [...arr].sort(sortByFn(attr, order));
/**
 * Finds the common elements in multiple arrays based on a key.
 * @template T
 * @param {keyof T} key - The key to use for comparison.
 * @param {...T[][]} arrays - The arrays to compare.
 * @returns {T[]} - The array of common elements.
 */
export const sameElementInArrays = (key, ...arrays) => {
    const firstArray = (arrays || []).slice(0, 1)[0] || [];
    const restArrays = arrays.length === 1 ? [firstArray] : (arrays || []).slice(-(arrays || []).length + 1);
    return firstArray.filter((item) => restArrays.every((arr) => arr && !!arr.find((el) => el[key] === item[key])));
};
/**
 * Splits an array into a specified number of parts.
 * @template T
 * @param {T[]} array - The array to split.
 * @param {number} numParts - The number of parts to split into.
 * @returns {T[][]} - The array of parts.
 */
export const splitArrayIntoParts = (array, numParts) => {
    const result = [];
    const totalLength = array.length;
    const baseSize = Math.floor(totalLength / numParts);
    const extraElements = totalLength % numParts;
    let index = 0;
    for (let i = 0; i < numParts; i += 1) {
        const chunkSize = baseSize + (i < extraElements ? 1 : 0);
        result.push(array.slice(index, index + chunkSize));
        index += chunkSize;
    }
    return result;
};
/**
 * Generates a seed string based on a salt and length.
 * @param {string} salt - The salt to use for the seed.
 * @param {number} [length=17] - The length of the seed.
 * @returns {string} - The generated seed.
 */
export const seed = (salt, length = 17) => [...Array(Math.ceil(length / 17)).keys()]
    .reduce((aggr, index) => {
    const seedNumber = Number.parseInt(`${index}${salt.substring(0, 100)}${index}`
        .split('')
        .map((char) => char.charCodeAt(0))
        .join(''), 10);
    const seedString = (((seedNumber * 9301 + 49297) % 233280) / 244280).toString().replace('0.', '').padEnd(17, '0').substring(0, 17);
    return `${aggr}${seedString}`;
}, '')
    .substring(0, length);
/**
 * Converts an object to an array of its values.
 * @template T
 * @param {{ [k: string]: T }} obj - The object to convert.
 * @returns {T[]} - The array of values.
 */
export const objectToArray = (obj) => Object.keys(obj).reduce((acc, key) => acc.concat([obj[key]]), []);
/**
 * Converts a string to a number.
 * Falls back to defaultValue and logs a message if conversion fails.
 *
 * @param logger - function to log invalid values
 * @returns a function that takes (value: string, defaultValue: number)
 *
 * @example
 * toNumber(console.warn)("42", 0); // 42
 * toNumber(console.warn)("toto", 0);  // 0 + logs warning
 */
export const toNumber = (logNaNFn) => (value, defaultValue) => {
    let result = Number(value);
    if (Number.isNaN(result)) {
        logNaNFn(`Invalid number value "${value}", falling back to default "${defaultValue}"`);
        result = defaultValue;
    }
    return result;
};
